<template>
  <AuthLayout
    eyebrow="Registrierung"
    title="Neuen Zugang anlegen"
    intro="Erstellen Sie Ihr Konto und starten Sie anschließend direkt mit Ihrem Vermarktungswunsch."
    card-title="Konto erstellen"
    card-intro="Legen Sie Ihre Anmeldedaten und Kontaktdaten fest. Danach können Sie Ihr Profil weiter ausbauen."
    :aside-list="[
      'Ihr Profil wird unter mein.mvp.de angelegt',
      'Sie können direkt mit Ihrer Vermarktung starten',
      'Ihre Daten bleiben jederzeit im Kundenbereich änderbar'
    ]"
    :wide="true"
  >
    <div v-if="userCreated" class="auth-success-panel">
      <div class="auth-success-panel__eyebrow">Erfolgreich registriert</div>
      <h3 class="auth-success-panel__title">Willkommen</h3>
      <p class="auth-success-panel__text">
        Die Registrierung war erfolgreich. Bitte prüfen Sie Ihre E-Mails für die weiteren Anweisungen.
      </p>
    </div>

    <Form v-else @submit="handleSubmit" v-slot="{ errors }" :validation-schema="schema" class="row g-3">
      <div v-if="apiErrors.length" class="col-12">
        <div class="alert alert-danger mb-0" role="alert" aria-live="polite">
          <div class="fw-semibold">Die Registrierung konnte nicht abgeschlossen werden:</div>
          <ul class="mb-0 mt-2 ps-3">
            <li v-for="message in apiErrors" :key="message">{{ message }}</li>
          </ul>
        </div>
      </div>

      <div class="col-12 col-lg-6">
        <div class="form-floating">
          <Field v-model="first_name" id="first_name" name="first_name" placeholder="Vorname" class="form-control" :class="{'is-invalid': errors.first_name}" />
          <label for="first_name">Vorname</label>
          <div v-if="errors.first_name" class="invalid-feedback">
            {{  errors.first_name }}
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-6">
        <div class="form-floating">
          <Field v-model="last_name" id="last_name" name="last_name" placeholder="Nachname" class="form-control" :class="{'is-invalid': errors.last_name}" />
          <label for="last_name">Nachname</label>
          <div v-if="errors.last_name" class="invalid-feedback">
            {{  errors.last_name }}
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="form-floating">
          <Field v-model="email" id="email" name="email" placeholder="E-Mail" class="form-control" :class="{'is-invalid': errors.email}" />
          <label for="email">E-Mail</label>
          <div v-if="errors.email" class="invalid-feedback">
            {{  errors.email }}
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="form-floating">
          <Field v-model="password" :type="showPassword ? 'text' : 'password'" id="password" name="password" placeholder="Passwort" autocomplete="new-password" class="form-control auth-password-input" :class="{'is-invalid': errors.password}" />
          <label for="password">Passwort</label>
          <div v-if="errors.password" class="invalid-feedback">
            {{  errors.password }}
          </div>
          <button
            type="button"
            class="auth-password-toggle"
            :class="{'auth-password-toggle--invalid': errors.password}"
            :aria-label="showPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'"
            :title="showPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'"
            :aria-pressed="showPassword"
            aria-controls="password"
            @click="showPassword = !showPassword"
          >
            <Icon :icon="showPassword ? 'fa-pro-solid:eye-slash' : 'fa-pro-solid:eye'" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div class="col-12">
        <div class="form-floating">
          <Field v-model="password_confirmation" :type="showPasswordConfirmation ? 'text' : 'password'" id="password_confirmation" name="password_confirmation" placeholder="Passwort wiederholen" autocomplete="new-password" class="form-control auth-password-input" :class="{'is-invalid': errors.password_confirmation}" />
          <label for="password_confirmation">Passwort wiederholen</label>
          <div v-if="errors.password_confirmation" class="invalid-feedback">
            {{  errors.password_confirmation }}
          </div>
          <button
            type="button"
            class="auth-password-toggle"
            :class="{'auth-password-toggle--invalid': errors.password_confirmation}"
            :aria-label="showPasswordConfirmation ? 'Passwort-Wiederholung ausblenden' : 'Passwort-Wiederholung anzeigen'"
            :title="showPasswordConfirmation ? 'Passwort-Wiederholung ausblenden' : 'Passwort-Wiederholung anzeigen'"
            :aria-pressed="showPasswordConfirmation"
            aria-controls="password_confirmation"
            @click="showPasswordConfirmation = !showPasswordConfirmation"
          >
            <Icon :icon="showPasswordConfirmation ? 'fa-pro-solid:eye-slash' : 'fa-pro-solid:eye'" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div class="col-12">
        <div class="auth-register-check">
          <Field v-model="confirm_agb" type="checkbox" :value="true" :unchecked-value="false" id="confirm_agb" name="confirm_agb" class="auth-register-check__input" :class="{'is-invalid': errors.confirm_agb}" />
          <label class="auth-register-check__label" for="confirm_agb">
            Ich akzeptiere die AGBs und Datenschutzbestimmungen und bestätige, dass mir diese vor der Registrierung zur Verfügung gestellt wurden.
          </label>
          <div v-if="errors.confirm_agb" class="invalid-feedback d-block">
            {{  errors.confirm_agb }}
          </div>
        </div>
        <div class="auth-register-terms">
          <a
            class="auth-register-terms__link"
            href="https://www.manet-marketing.de/downloads-fuer-kunden/"
            target="_blank"
            rel="noopener noreferrer"
          >AGB</a>
          <a
            class="auth-register-terms__link"
            href="https://www.manet-marketing.de/downloads-fuer-kunden/"
            target="_blank"
            rel="noopener noreferrer"
          >Datenschutzerklärung</a>
        </div>
      </div>

      <div class="col-12">
        <MatchaWidget v-model="matchaToken" />
      </div>

      <div class="col-12 col-xl-7">
        <p class="auth-register-note">
          Nach diesem Schritt wird Ihr Profil unter mein.mvp.de angelegt. Danach können Sie Ihren Vermarktungswunsch starten und später jederzeit wieder einsteigen.
        </p>
      </div>

      <div class="col-12 col-xl-5 d-flex align-items-end">
        <button type="submit" class="btn btn-primary btn-block" :disabled="submitting">
          {{ submitting ? 'Konto wird angelegt …' : 'Anlegen und weiter' }}
        </button>
      </div>
    </Form>
  </AuthLayout>
</template>

<script>
import {useUserStore} from "../stores/user";
import {Field, Form} from 'vee-validate';
import AuthLayout from "@/components/layout/AuthLayout.vue";
import {registrationApiErrors, registrationSchema} from '@/validation/registration';
import MatchaWidget from "@/components/auth/MatchaWidget.vue";

export default {
  components: {
    Field,
    Form,
    AuthLayout,
    MatchaWidget,
  },
  setup() {
    const userStore = useUserStore()
    return {userStore}
  },
  data() {
    return {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      password_confirmation: "",
      confirm_agb: false,
      showPassword: false,
      showPasswordConfirmation: false,
      submitting: false,
      matchaToken: '',
      apiErrors: [],
      schema: registrationSchema,
    }
  },
  computed: {
    userCreated() {
      return this.userStore.created;
    },
  },
  methods: {
    async handleSubmit(values) {
      this.apiErrors = [];
      this.submitting = true;

      try {
        const response = await this.userStore.register({
          email: values.email.trim(),
          password: values.password,
          password_confirmation: values.password_confirmation,
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
        }, this.matchaToken);

        if (response?.error) {
          this.apiErrors = registrationApiErrors(response);
        }
      } catch (error) {
        this.apiErrors = registrationApiErrors(error?.response?.data);
      } finally {
        if (!this.apiErrors.length && !this.userCreated) {
          this.apiErrors = ['Die Registrierung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.'];
        }
        this.submitting = false;
      }
    }
  },
}
</script>

<style scoped lang="scss">
.auth-success-panel {
  padding: 1.25rem;
  border: 1px solid rgba(12, 58, 83, 0.12);
  background: rgba(13, 79, 113, 0.04);
}

.auth-success-panel__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #6f8799;
}

.auth-success-panel__title {
  margin: 0.45rem 0 0.6rem;
  font-size: clamp(1.75rem, 3vw, 2.2rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: #123754;
}

.auth-success-panel__text,
.auth-register-note {
  margin: 0;
  color: #5f7283;
}

.auth-password-input {
  padding-right: 3.25rem;
}

.auth-password-toggle {
  position: absolute;
  z-index: 5;
  top: 0.75rem;
  right: 0.65rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #5f7283;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(13, 79, 113, 0.08);
    color: #0d4f71;
  }

  &:focus-visible {
    outline: 2px solid rgba(13, 79, 113, 0.35);
    outline-offset: 1px;
  }

  &--invalid {
    right: 2.2rem;
  }
}

.auth-register-check {
  display: grid;
  grid-template-columns: 1.125rem 1fr;
  column-gap: 0.65rem;
  row-gap: 0.35rem;
  align-items: center;
  padding: 0;
  margin: 0;

  &__input {
    grid-column: 1;
    grid-row: 1;
    width: 1.125rem;
    height: 1.125rem;
    margin: 0;
    border-radius: 0;
    border-color: rgba(12, 58, 83, 0.16);
    flex-shrink: 0;
  }

  &__label {
    grid-column: 2;
    grid-row: 1;
    margin: 0;
    padding: 0;
    line-height: 1.125rem;
    color: #123754;
    cursor: pointer;
  }

  .invalid-feedback {
    grid-column: 1 / -1;
    grid-row: 2;
  }
}

.auth-register-terms {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem 1.25rem;
  margin-top: 0.75rem;
}

.auth-register-terms__link {
  padding: 0;
  border: 0;
  background: none;
  color: #1b6c97;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.4;
  text-decoration: underline;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: #0d4f71;
  }
}
</style>
